"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { CabHiringList, toCabHiringQueryParams, type CabHiringFilters } from "./list"
import axiosInstance from "../../../libs/axios"

import { useUrlFilters } from "../../../hooks/useUrlFilters";
import { URL_FILTER_KEYS } from "./list";
import { useDebounce } from "../../../hooks/useDebounce"

const CabHiringClaim: React.FC = () => {
  const [trips, setTrips] = useState<any[]>([])
  const [pagination, setPagination] = useState({
    total_count: 0,
    page: 1,
    limit: 10,
    total_pages: 0,
    has_next_page: false,
    has_prev_page: false,
    next_page: 0,
    prev_page: 0,
  })
  const [loading, setLoading] = useState(false)
  // Seeded from the URL so a dashboard tile can open this list already
  // filtered to the rows it counted (e.g. ?status=claim_submitted).
  // URL_FILTER_KEYS lives in ./list.
  const {
    status: urlStatus = "",
    search: urlSearch = "",
    ...urlFilters
  } = useUrlFilters<CabHiringFilters & { status?: string; search?: string }>({}, URL_FILTER_KEYS)
  const [statusFilter, setStatusFilter] = useState(urlStatus)
  const [searchTerm, setSearchTerm] = useState(urlSearch)
  const debouncedSearch = useDebounce(searchTerm, 400)
  const [advancedFilters, setAdvancedFilters] = useState<CabHiringFilters>(urlFilters)
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: "ASC" | "DESC" }>({
    field: "trip_start_date",
    direction: "DESC",
  })

  const fetchTrips = async (
    page = pagination.page,
    status = statusFilter,
    search = searchTerm,
    limitValue?: number,
    filters = advancedFilters,
    sort = sortConfig
  ) => {
    try {
      setLoading(true)
      const effectiveLimit = limitValue ?? pagination.limit ?? 10

      const params: any = {
        page: page,
        limit: effectiveLimit,
        ...toCabHiringQueryParams(filters),
      }

      if (status) params.status = status
      if (search) params.search = search
      if (sort?.field) {
        params.sort_by = sort.field
        params.sort_order = sort.direction
      }

      const response = await axiosInstance.get("/claims/cab-hiring/list", { params })

      if (response.data.success) {
        const cabHirings = response.data.data?.cabHirings || []
        const tripsArray = Array.isArray(cabHirings) ? cabHirings : []
        setTrips(tripsArray)
        setPagination(response.data.data?.pagination || pagination)
      } else {
        setTrips([])
      }
    } catch (error) {
      console.error("Error fetching cab hiring trips:", error)
      setTrips([])
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (search: string) => {
    setSearchTerm(search)
    // API call triggered by debouncedSearch effect below
  }

  // Fire fetch when debounced search value settles (also the initial load)
  useEffect(() => {
    fetchTrips(1, statusFilter, debouncedSearch, undefined, advancedFilters, sortConfig)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  const handleFilterChange = (filter: string) => {
    setStatusFilter(filter)
    fetchTrips(1, filter, searchTerm, undefined, advancedFilters, sortConfig)
  }

  const handleAdvancedFilter = (filters: CabHiringFilters) => {
    setAdvancedFilters(filters)
    fetchTrips(1, statusFilter, searchTerm, undefined, filters, sortConfig)
  }

  const handlePageChange = (page: number) => {
    fetchTrips(page, statusFilter, searchTerm, undefined, advancedFilters, sortConfig)
  }

  const handleLimitChange = (limit: number) => {
    setPagination((prev) => ({ ...prev, limit, page: 1 }))
    fetchTrips(1, statusFilter, searchTerm, limit, advancedFilters, sortConfig)
  }

  const handleSort = (field: string, direction: "ASC" | "DESC") => {
    const sort = { field, direction }
    setSortConfig(sort)
    fetchTrips(pagination.page, statusFilter, searchTerm, undefined, advancedFilters, sort)
  }

  return (
    <div>
      <CabHiringList
        trips={trips}
        pagination={pagination}
        onSearch={handleSearch}
        onFilterChange={handleFilterChange}
        statusFilter={statusFilter}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onAdvancedFilter={handleAdvancedFilter}
        loading={loading}
        sortConfig={sortConfig}
        onSort={handleSort}
        appliedFilters={advancedFilters}
        searchValue={searchTerm}
      />
    </div>
  )
}

export default CabHiringClaim
